import React, { ReactNode } from "react";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { CircleBox } from "@features/recommender/common";
import { LinearProgressBar } from "@rentablo/components";
import { toPercent } from "@helpers";
import { LegendBlock } from "../../LegendBlock";

const getColorKey = (index: number) => (index === 0 ? "02" : "06");
const getColorIndex = (index: number) => {
  const totalColors = 14;
  const cycledIndex = index % totalColors;

  if (cycledIndex < 9) {
    return `0${cycledIndex + 1}`;
  } else {
    return `${cycledIndex + 1}`;
  }
};

interface Item {
  logo: ReactNode;
  value: number;
}

export interface ItemGroup {
  name: string;
  items: Item[];
}

interface Props {
  title: string;
  showMore: boolean;
  investmentName: string;
  recommendationName: string;
  items?: ItemGroup[];
}

export const BreakdownDetails = ({
  title,
  showMore,
  investmentName,
  recommendationName,
  items,
}: Props) => {
  const theme = useTheme();

  return (
    <Collapse in={showMore}>
      <Typography variant="subtitle1" color="textPrimary" mt={4} mb={4.5}>
        {title}
      </Typography>
      <LegendBlock
        ogFundName={investmentName}
        recommendationName={recommendationName}
        margin={theme.spacing(0, 0, 5, 0)}
      />
      {items?.map((parentItem, index) => (
        <Box
          key={parentItem.name}
          p={3}
          borderTop={`1px solid ${theme.palette.border.secondary}`}
          borderBottom={
            index !== items.length - 1
              ? "none"
              : `1px solid ${theme.palette.border.secondary}`
          }
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={1.5}
            mb={2}
          >
            <CircleBox
              sx={{
                backgroundColor:
                  theme.palette.graphVisual[getColorIndex(index)]?.mid,
                height: "14px",
                width: "14px",
              }}
            />
            <Typography variant="body1">{parentItem.name}</Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            {parentItem.items.map((childItem, i) => (
              <Box
                key={i}
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={2.5}
              >
                {childItem.logo}
                <LinearProgressBar
                  progress={childItem.value * 100}
                  thickness={8}
                  borderRadius={theme.spacing(1)}
                  bgColor={theme.palette.border.secondary}
                  color={theme.palette.graphVisual[getColorKey(i)]?.mid}
                />
                <Typography variant="body3">
                  {toPercent(childItem.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Collapse>
  );
};
